import type { SearchType } from '@brightdrop/bd-ui';

import type { SafetyEventHistoryFilters } from '~/routes/organizations/safety/models/safetyEventHistory.model';

/**
 * Maps a page index to the continuation token that fetches that page.
 *
 * The safety events endpoint is cursor paginated, so a page index on its own is
 * not enough to re-request a page. Page 0 has no token.
 */
export type SafetyEventHistoryContinuationTokens = Record<
  number,
  string | undefined
>;

/**
 * Shared empty token map.
 *
 * Reused rather than writing a fresh `{}` so that clearing an already-clear map
 * does not change identity and re-trigger effects that depend on it.
 */
export const EMPTY_SAFETY_EVENT_HISTORY_CONTINUATION_TOKENS: SafetyEventHistoryContinuationTokens =
  {};

/**
 * View state for one safety event history list, scoped to a session id.
 *
 * Held in Redux rather than component state so the list survives a remount, for
 * example when the user opens an event and presses browser back. Page size is
 * deliberately absent: it is derived from a feature flag at render time and a
 * stored copy could go stale.
 */
export type SafetyEventHistoryViewSession = {
  filters?: Partial<SafetyEventHistoryFilters>;
  searchQuery?: string;
  searchType?: SearchType;
  pageIndex?: number;
  continuationTokens?: SafetyEventHistoryContinuationTokens;
};

/** All safety event history view sessions, keyed by session id. */
export type SafetyEventHistoryState = {
  sessionConfigs: Record<string, SafetyEventHistoryViewSession>;
};

/** Payload for a partial write to one session's view state. */
export type SetSafetyEventHistorySessionPayload = {
  sessionId: string;
  config: Partial<SafetyEventHistoryViewSession>;
};

/** Starting state: no sessions, so every list opens on its defaults. */
export const INITIAL_SAFETY_EVENT_HISTORY_STATE: SafetyEventHistoryState = {
  sessionConfigs: {},
};
